import { ModalDS, Spacer, TextCapitalized } from "@qivia/ui";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import "../../../i18n/i18n";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  getCardPinEphemeralKeyAsync,
  getCardProviderDataAsync,
  viewPinCodeSlice,
} from "../viewPinCodeSlice";
import type { RootState } from "../../../redux/store";

export const ViewPinCodeModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
  cardUuid: string | null;
  cardLabel?: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [stripeLoaded, setStripeLoaded] = useState<Stripe | null>(null);
  const cardUuid = props.cardUuid;

  const {
    cardProvider,
    accountProvider,
    nonce,
    ephemeralKey,
    getProviderDataStatus,
    getEphemeralKeyStatus,
  } = useAppSelector((state: RootState) => state.viewPinCode);

  const stripePublicKey = useMemo(
    () => String(import.meta.env["VITE_STRIPE_PUBLIC_KEY"] ?? ""),
    [],
  );
  const reset = useCallback(() => {
    setStripeLoaded(null);
    dispatch(viewPinCodeSlice.actions.resetAll());
  }, [dispatch]);

  const onClose = useCallback(() => {
    reset();
    props.onCloseModal();
  }, [props, reset]);

  useEffect(() => {
    if (!props.visible || !cardUuid) return;
    reset();
    void dispatch(getCardProviderDataAsync({ cardUuid }));
  }, [cardUuid, dispatch, props.visible, reset]);

  useEffect(() => {
    if (!props.visible || !accountProvider) return;
    if (getProviderDataStatus !== "success") return;
    if (stripePublicKey.length === 0) {
      return;
    }

    setStripeLoaded(null);
    void loadStripe(stripePublicKey, {
      apiVersion: "2022-11-15",
      stripeAccount: accountProvider,
    }).then((stripe) => {
      if (!stripe) {
        return;
      }
      setStripeLoaded(stripe);
    });
  }, [accountProvider, getProviderDataStatus, props.visible, stripePublicKey]);

  useEffect(() => {
    if (!props.visible || !stripeLoaded || !cardProvider) return;
    if (nonce) return;
    void stripeLoaded
      .createEphemeralKeyNonce({
        issuingCard: cardProvider,
      })
      .then((keyNonce: { nonce?: string | null }) => {
        if (keyNonce.nonce) {
          dispatch(
            viewPinCodeSlice.actions.storeNonce({ nonce: keyNonce.nonce }),
          );
        }
      });
  }, [cardProvider, dispatch, nonce, props.visible, stripeLoaded]);

  useEffect(() => {
    if (!props.visible || !cardUuid || !nonce) return;
    if (getEphemeralKeyStatus !== "idle") return;
    void dispatch(getCardPinEphemeralKeyAsync({ cardUuid, nonce }));
  }, [cardUuid, dispatch, getEphemeralKeyStatus, nonce, props.visible]);

  useEffect(() => {
    if (
      !props.visible ||
      !stripeLoaded ||
      !cardProvider ||
      !nonce ||
      !ephemeralKey
    )
      return;

    const elements = stripeLoaded.elements();
    const pinDisplay = elements.create("issuingCardPinDisplay", {
      issuingCard: cardProvider,
      nonce,
      ephemeralKeySecret: ephemeralKey,
      style: {
        base: {
          fontSize: "28px",
          letterSpacing: "12px",
          lineHeight: "48px",
        },
      },
    });
    pinDisplay.mount("#card-pin");

    return () => {
      if (pinDisplay) {
        void pinDisplay.unmount();
      }
    };
  }, [cardProvider, ephemeralKey, nonce, props.visible, stripeLoaded]);

  return (
    <ModalDS visible={props.visible} onClose={onClose} width={40}>
      <StyledContainer>
        <TextCapitalized>
          {t("cards_shipments.pin.modal.viewPin.title")} {props.cardLabel ?? ""}
        </TextCapitalized>
        <Spacer y={1.5} />
        <StyledPin>
          <div id="card-pin" />
        </StyledPin>
        <Spacer y={1.5} />
      </StyledContainer>
    </ModalDS>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  justify-content: center;
`;
const StyledPin = styled.div`
  width: 8rem;
  height: 3.3rem;
`;
